import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    targetId: String
  }
  static classes = ["show"]

  connect() {
    this.body = document.querySelector("body")
    const target = this.target
    this.keydownHandler = this.handleKeydown.bind(this)
    
    // Set initial aria-expanded state on the controller element
    if (this.element instanceof HTMLElement) {
      this.element.setAttribute("aria-expanded", "false")
    }
    
    // Set aria-hidden on the target dialog
    if (target) {
      target.setAttribute("aria-hidden", "true")
    }
    
    // If this is a wrapper element (target is a child), listen for target's state changes
    if (target && this.element.contains(target) && this.element !== target) {
      this.targetElement = target
      this.observeTargetChanges()
    }
  }

  observeTargetChanges() {
    // Watch for class changes on the target to sync aria-expanded on wrapper
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          const isOpen = this.targetElement.classList.contains(this.showClass)
          this.element.setAttribute("aria-expanded", isOpen ? "true" : "false")
        }
      })
    })
    
    observer.observe(this.targetElement, { attributes: true })
    this.observer = observer
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }

    this.target?.removeEventListener("keydown", this.keydownHandler)
  }

  open(event) {
    const target = this.target
    if (!target) return

    this.lastFocusedElement = event?.currentTarget instanceof HTMLElement ? event.currentTarget : document.activeElement
    target.dialogTrigger = this.lastFocusedElement
    target.classList.add(this.showClass)
    this.previousBodyOverflow = this.body.style.overflow
    this.body.style.overflow = "hidden"

    // Set aria-expanded on the element with the controller
    if (this.element instanceof HTMLElement) {
      this.element.setAttribute("aria-expanded", "true")
    }
    
    // Set aria-hidden on the dialog (if different from controller element)
    if (target && target !== this.element) {
      target.setAttribute("aria-hidden", "false")
    }

    const data = event?.currentTarget?.dataset || {}
    this.dispatch("open", { detail: { ...data, target } })
    this.bindDialogFocus(target)
    
    const dialogTitle = target.querySelector('[role="heading"], h1, h2, h3')?.textContent || this.element.dataset.dialogLabel || "Dialog"
    const openedText = this.element.dataset.dialogOpenedText || "opened"
    this.announce(`${dialogTitle} ${openedText}`)
  }

  close() {
    const target = this.target
    if (!target) return

    target.classList.remove(this.showClass)
    this.body.style.overflow = this.previousBodyOverflow || ""
    target.removeEventListener("keydown", this.keydownHandler)

    // Set aria-expanded on the element with the controller
    if (this.element instanceof HTMLElement) {
      this.element.setAttribute("aria-expanded", "false")
    }
    
    // Set aria-hidden on the dialog (if different from controller element)
    if (target && target !== this.element) {
      target.setAttribute("aria-hidden", "true")
    }

    this.dispatch("close", { detail: { target } })
    const message = this.element.dataset.dialogClosedMessage || "Dialog closed"
    this.announce(message)

    const focusTarget = target.dialogTrigger || this.lastFocusedElement
    if (focusTarget instanceof HTMLElement && document.contains(focusTarget)) {
      focusTarget.focus()
    }
  }

  toggle(event) {
    const target = this.target
    if (!target) return

    if (target.classList.contains(this.showClass)) {
      this.close()
    } else {
      this.open(event)
    }
  }

  get target() {
    return document.querySelector(this.targetIdValue)
  }

  bindDialogFocus(target) {
    target.addEventListener("keydown", this.keydownHandler)

    if (!target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1")
    }

    const focusableElements = this.focusableElements(target)
    const firstFocusableElement = focusableElements[0] || target
    firstFocusableElement.focus()
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key !== "Tab") return

    const focusableElements = this.focusableElements(event.currentTarget)
    if (focusableElements.length === 0) {
      event.preventDefault()
      event.currentTarget.focus()
      return
    }

    const firstFocusableElement = focusableElements[0]
    const lastFocusableElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstFocusableElement) {
      event.preventDefault()
      lastFocusableElement.focus()
    } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
      event.preventDefault()
      firstFocusableElement.focus()
    }
  }

  focusableElements(target) {
    return Array.from(target.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true")
  }

  announce(message) {
    window.dispatchEvent(new CustomEvent("aria-announcer:announce", {
      detail: { message, priority: "polite" }
    }))
  }
}
